import { memo, useState, useRef, lazy, Suspense } from "react";
import { useAttributionOnView, useAttribution } from "@/hooks/useAttribution";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import {
  MessageCircle,
  Share2,
  ThumbsUp,
  ThumbsDown,
  MapPin,
  Bookmark,
  ShoppingCart,
  Crown,
  Utensils,
  Calendar,
  Gift,
  ChefHat,
  ChevronLeft,
  ChevronRight,
  Copy,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { KudozIcon } from "@/components/KudozIcon";
const CommentsSheet = lazy(() => import("./CommentsSheet").then((m) => ({ default: m.CommentsSheet })));
const ShareReviewModal = lazy(() => import("./ShareReviewModal"));
const OrderInitiationModal = lazy(() => import("./OrderInitiationModal"));
import { cn, resolveEatzyMediaUrl, formatOfferExpiry, getOfferExpiryUrgency, type OfferExpiryUrgency } from "@/lib/utils";
import { LazyImage } from "@/components/LazyImage";
import { AuthenticatedVideo } from "@/components/AuthenticatedVideo";
import {
  useRestaurantBookmark,
  useIsRestaurantBookmarked,
} from "@/hooks/useRestaurantBookmark";
import { useToast } from "@/hooks/use-toast";
import { useSessionLocation } from "@/hooks/useSessionLocation";
import type { LightboxEntry } from "./MediaLightbox";
const MediaLightbox = lazy(() => import("./MediaLightbox").then((m) => ({ default: m.MediaLightbox })));
import { ReadMoreText } from "./ReadMoreText";
import { extractKudozMultiplier } from "@shared/kudozMultiplier";
import type { PromotionDetails as SharedPromotionDetails } from "@shared/schema";
import { FEED_MEDIA_FRAME_CLASSES } from "@shared/feedMediaFrame";

interface PromotionDetails {
  type?: "event" | "promotion" | "menu_update" | "chef_special";
  title?: string;
  couponCode?: string;
  expiresAt?: string;
  eventDate?: string;
  kudozMultiplier?: number;
  kudozBonus?: number | "2x";
  smartFixEdited?: { version: number; kind: string; verified: boolean; assets: { assetId: string; mediaIndex: number }[] };
}

/**
 * Format a Kudoz multiplier for display (e.g. 2 → "2x", 1.5 → "1.5x", 3 → "3x").
 * Strips trailing zeros from decimals so 2.0 renders as "2x" rather than "2.0x".
 */
function formatKudozMultiplier(multiplier: number): string {
  const rounded = Math.round(multiplier * 100) / 100;
  const str = Number.isInteger(rounded)
    ? `${rounded}`
    : `${rounded}`.replace(/\.?0+$/, "");
  return `${str}x`;
}

function isOfferExpired(dateStr: string): boolean {
  return new Date(dateStr) < new Date();
}

interface ItemType {
  name: string;
  description?: string;
  price?: number;
  image?: string;
  itemRating?: "loved" | "fine" | "disliked";
  itemRatingText?: string;
  averageRating?: number;
  totalRatings?: number;
  isCrowdFavorite?: boolean;
  isTheBest?: boolean;
}

interface RestaurantPostCardProps {
  id: string;
  restaurant: {
    id: string;
    name: string;
    location: string;
    overallRating: number;
    totalReviews: number;
    logo?: string;
    orderingEnabled?: boolean;
  };
  text: string;
  images?: string[];
  image?: string;
  video?: string;
  mediaUrl?: string;
  mediaType?: string;
  items?: ItemType[];
  createdAt: string;
  likes: number;
  comments: number;
  helpfulUpVotes?: number;
  helpfulDownVotes?: number;
  helpfulVotes?: number;
  tags: string[];
  promotionDetails?: PromotionDetails;
  /**
   * Effective Kudoz multiplier for engaging with this post (e.g. 1.5, 2, 3).
   * When omitted, falls back to deriving from `promotionDetails`.
   * The badge is hidden when the resolved multiplier is <= 1 or absent.
   */
  kudozMultiplier?: number | null;
  featured?: boolean;
  isLiked?: boolean;
  isHelpful?: boolean | null;
  isBookmarked?: boolean;
}

const KB_PRESETS = [
  { name: "kb-pan-right", duration: 8 },
  { name: "kb-pan-left", duration: 8 },
  { name: "kb-pan-down", duration: 7 },
  { name: "kb-pan-up", duration: 7 },
  { name: "kb-zoom-in", duration: 9 },
  { name: "kb-zoom-out", duration: 9 },
  { name: "kb-zoom-pan-tr", duration: 10 },
  { name: "kb-zoom-pan-bl", duration: 10 },
];

const promotionIcons = {
  event: Calendar,
  promotion: Gift,
  menu_update: Utensils,
  chef_special: ChefHat,
};

function ItemInfoOverlay({ item }: { item: ItemType }) {
  const [expanded, setExpanded] = useState(false);
  const description = item.description || "";
  const hasDescription = description.length > 0;

  return (
    <div
      className="absolute top-3 left-3"
      style={{ maxWidth: "calc(100% - 120px)" }}
    >
      <div className="bg-black/70 backdrop-blur-sm rounded-md px-3 py-1.5">
        <div className="text-white text-sm font-medium truncate">
          {item.name}
        </div>
        {hasDescription && (
          <div className="mt-0.5 flex items-baseline gap-1">
            {expanded ? (
              <span className="text-white/80 text-xs leading-tight">
                {description}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpanded(false);
                  }}
                  className="ml-1 text-xs text-primary font-medium hover:underline"
                >
                  less
                </button>
              </span>
            ) : (
              <>
                <span className="text-white/80 text-xs truncate flex-1 min-w-0">
                  {description}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpanded(true);
                  }}
                  className="text-xs text-primary font-medium hover:underline flex-shrink-0"
                >
                  more
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function RestaurantPostCardImpl({
  id,
  restaurant,
  text,
  images,
  image,
  video,
  mediaUrl,
  mediaType,
  items,
  createdAt,
  likes: _likes,
  comments,
  helpfulUpVotes = 0,
  helpfulDownVotes = 0,
  helpfulVotes = 0,
  tags,
  promotionDetails,
  kudozMultiplier,
  featured = false,
  isLiked: _isLiked = false,
  isHelpful = null,
  isBookmarked: _isBookmarked = false,
}: RestaurantPostCardProps) {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [helpful, setHelpful] = useState<boolean | null>(isHelpful);
  const [upVotes, setUpVotes] = useState(helpfulUpVotes || helpfulVotes);
  const [downVotes, setDownVotes] = useState(helpfulDownVotes);
  const [showComments, setShowComments] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const videoContainerRef = useRef<HTMLDivElement>(null);

  // Attribution: fire post_view once the card becomes ≥50% visible.
  // Avoids the request storm of firing on every card mount at feed load.
  const attributionRef = useAttributionOnView<HTMLDivElement>(
    "post_view",
    "post",
    id,
  );
  const { sendAttributionEvent } = useAttribution();

  // Ken Burns: pick a random preset each time the visible image changes
  const kbPresetRef = useRef<{ name: string; duration: number } | null>(null);
  const kbLastIndexRef = useRef<number>(-1);
  if (
    kbLastIndexRef.current !== currentImageIndex ||
    kbPresetRef.current === null
  ) {
    kbLastIndexRef.current = currentImageIndex;
    kbPresetRef.current =
      KB_PRESETS[Math.floor(Math.random() * KB_PRESETS.length)];
  }
  const kbStyle = {
    animationName: kbPresetRef.current.name,
    animationDuration: `${kbPresetRef.current.duration}s`,
    animationIterationCount: "infinite",
    animationDirection: "alternate" as const,
  };

  const { toggleBookmark } = useRestaurantBookmark(restaurant.id);
  const bookmarked = useIsRestaurantBookmarked(restaurant.id);

  // Fetch restaurant status to determine if it's open
  // Pre-fetch for ordering-enabled restaurants so status is ready when modal opens
  const { data: restaurantData, isLoading: restaurantLoading } = useQuery<{
    liveStatus?: { open: boolean };
    eatzyTimezone?: string | null;
    hoursJson?: Record<string, unknown> | null;
  }>({
    queryKey: ["/api/restaurants", restaurant.id],
    enabled: restaurant.orderingEnabled !== false,
  });
  // Default to undefined while loading to show loading state in modal
  // Don't default to true when data is missing - let the modal handle undefined state
  const isRestaurantOpen = restaurantLoading
    ? undefined
    : restaurantData?.liveStatus?.open;

  const handleHelpful = (vote: boolean) => {
    const previousHelpful = helpful;

    if (previousHelpful === vote) {
      if (vote) {
        setUpVotes(upVotes - 1);
      } else {
        setDownVotes(downVotes - 1);
      }
      setHelpful(null);
    } else if (previousHelpful === null) {
      if (vote) {
        setUpVotes(upVotes + 1);
      } else {
        setDownVotes(downVotes + 1);
      }
      setHelpful(vote);
    } else {
      if (vote) {
        setDownVotes(downVotes - 1);
        setUpVotes(upVotes + 1);
      } else {
        setUpVotes(upVotes - 1);
        setDownVotes(downVotes + 1);
      }
      setHelpful(vote);
    }
  };

  const handleComment = () => {
    setShowComments(true);
  };

  const handleShare = () => {
    setShowShareModal(true);
  };

  const handleBookmark = () => {
    toggleBookmark();

    if (!bookmarked) {
      toast({
        description: (
          <div className="flex items-center gap-2">
            <Bookmark className="w-4 h-4 fill-current text-kudoz" />
            <span>Saved {restaurant.name}</span>
          </div>
        ),
      });
    } else {
      toast({
        description: `Removed ${restaurant.name} from saved`,
      });
    }
  };

  const handleOrder = () => {
    setShowOrderModal(true);
  };

  // Session-location wiring — see ReviewCard.tsx for rationale. Keeps the user's previously
  // chosen delivery address pre-selected in the order modal instead of silently snapping to primary.
  const {
    selectedLocationName: sessionLocationName,
    customLatitude: sessionLat,
    customLongitude: sessionLng,
    selectedAddressId: sessionAddressId,
    setSessionLocation,
  } = useSessionLocation();
  const sessionCoordinates =
    typeof sessionLat === "number" && typeof sessionLng === "number"
      ? { lat: sessionLat, lng: sessionLng }
      : null;

  // Handler for order modal proceed
  const handleOrderProceed = (settings: {
    serviceType: "delivery" | "takeout";
    timing: "asap" | "scheduled";
    scheduledTime?: string;
    location: string;
    coordinates?: { lat: number; lng: number };
    addressId?: number;
  }) => {
    // Build URL with order context parameters - match Restaurant page's expected param names
    const params = new URLSearchParams();
    params.set("serviceType", settings.serviceType);
    params.set("timing", settings.timing);
    params.set("location", settings.location);
    if (settings.scheduledTime) {
      params.set("scheduledTime", settings.scheduledTime);
    }
    if (settings.coordinates) {
      params.set("lat", settings.coordinates.lat.toString());
      params.set("lng", settings.coordinates.lng.toString());
    }
    if (typeof settings.addressId === "number") {
      params.set("addressId", settings.addressId.toString());
    }

    // Navigate to restaurant page with order context
    navigate(`/restaurant/${restaurant.id}?${params.toString()}`);
  };

  // Helper to ensure media URLs are properly prefixed for serving
  const resolveMediaUrl = (url: string | undefined): string | null => {
    if (!url) return null;
    const rewritten = resolveEatzyMediaUrl(url);
    if (rewritten !== url) return rewritten ?? null;
    if (
      url.startsWith("/api/media/") ||
      url.startsWith("http://") ||
      url.startsWith("https://")
    ) {
      return url;
    }
    if (url.startsWith("public/")) {
      return `/api/media/${url}`;
    }
    return url;
  };

  // Helper: detect video by URL extension or explicit flag
  const isVideoUrl = (url: string) => /\.(mp4|webm|mov)$/i.test(url);

  // Build carousel media: general media first, then item media
  const getPostMedia = (): {
    url: string;
    item?: ItemType;
    isVideo: boolean;
  }[] => {
    // Pre-compute the set of URLs already covered by labelled items so we can
    // skip bare duplicates in the general-media / mediaUrl sections below.
    // This prevents Bug B: when buildPartnerSpecialsItems claims all captioned
    // media into items[], images[] becomes empty and the else-if(mediaUrl)
    // branch would prepend the hero image unlabelled — then the items block
    // adds it again with a label, duplicating the first slide.
    const itemUrls = new Set<string>();
    if (items && items.length > 0) {
      items.forEach((item) => {
        const resolved = resolveMediaUrl(item.image);
        if (resolved) itemUrls.add(resolved);
      });
    }

    const mediaItems: { url: string; item?: ItemType; isVideo: boolean }[] = [];

    // Add general media first (from images array or mediaUrl).
    // Skip any URL already covered by a labelled item — it will appear below
    // with its item overlay and must not appear twice as a bare slide.
    if (images && images.length > 0) {
      images.forEach((img) => {
        const resolved = resolveMediaUrl(img);
        if (resolved && !itemUrls.has(resolved))
          mediaItems.push({ url: resolved, isVideo: isVideoUrl(resolved) });
      });
    } else if (mediaUrl) {
      const resolved = resolveMediaUrl(mediaUrl);
      if (resolved && !itemUrls.has(resolved))
        mediaItems.push({
          url: resolved,
          isVideo: mediaType === "video" || isVideoUrl(resolved),
        });
    }

    // Add item-level media
    if (items && items.length > 0) {
      items.forEach((item) => {
        const resolved = resolveMediaUrl(item.image);
        if (resolved)
          mediaItems.push({
            url: resolved,
            item,
            isVideo: isVideoUrl(resolved),
          });
      });
    }

    // Fallback to legacy fields
    if (mediaItems.length === 0) {
      if (image) {
        const resolved = resolveMediaUrl(image);
        if (resolved) mediaItems.push({ url: resolved, isVideo: false });
      }
      if (video) {
        const resolved = resolveMediaUrl(video);
        if (resolved) mediaItems.push({ url: resolved, isVideo: true });
      }
    }

    return mediaItems;
  };

  const postMedia = getPostMedia();
  const hasMultipleImages = postMedia.length > 1;

  // Lightbox: photo-only entries derived from postMedia
  const lightboxEntries: LightboxEntry[] = postMedia
    .filter((m) => !m.isVideo)
    .map((m) => ({
      url: m.url,
      isVideo: false,
      item: m.item
        ? {
            name: m.item.name,
            price: m.item.price ?? 0,
            itemRating: (m.item.itemRating ?? "fine") as
              | "loved"
              | "fine"
              | "disliked",
            itemRatingText: m.item.itemRatingText,
            isTheBest: m.item.isTheBest ?? false,
          }
        : null,
    }));

  // Map carousel index → lightbox photo index (skipping video entries)
  const openLightboxAtCarouselIndex = (carouselIdx: number) => {
    if (postMedia[carouselIdx]?.isVideo) {
      // For video slides: request native fullscreen on the <video> element
      const videoEl = videoContainerRef.current?.querySelector("video");
      videoEl?.requestFullscreen?.();
      return;
    }
    // Count how many non-video entries precede this carousel index
    const photoIdx = postMedia
      .slice(0, carouselIdx)
      .filter((m) => !m.isVideo).length;
    if (lightboxEntries.length === 0) return;
    setLightboxIndex(Math.min(photoIdx, lightboxEntries.length - 1));
    setLightboxOpen(true);
  };

  // Get current item info if showing item-level media
  const currentMediaItem = postMedia[currentImageIndex];
  const currentItem = currentMediaItem?.item || null;

  const PromotionIcon = promotionDetails?.type
    ? promotionIcons[promotionDetails.type]
    : null;
  const isEvent = promotionDetails?.type === "event";
  const eventDate = promotionDetails?.eventDate;
  const showHeadlineRow = !!(
    promotionDetails?.title ||
    featured ||
    (isEvent && eventDate)
  );

  // Resolve effective multiplier:
  // 1. If the explicit `kudozMultiplier` prop is provided (non-null/non-undefined), it is
  //    authoritative — even when <= 1, which means "hide the badge", and overrides any
  //    legacy `promotionDetails.kudozBonus === '2x'` value.
  // 2. Otherwise, derive from `promotionDetails` via the shared helper (which itself has
  //    the same precedence: explicit `kudozMultiplier` field overrides legacy `kudozBonus`).
  // Anything <= 1 ultimately hides the badge.
  const resolvedKudozMultiplier: number | null = (() => {
    if (kudozMultiplier !== undefined && kudozMultiplier !== null) {
      if (
        typeof kudozMultiplier === "number" &&
        Number.isFinite(kudozMultiplier) &&
        kudozMultiplier > 1
      ) {
        return kudozMultiplier;
      }
      return null;
    }
    return extractKudozMultiplier(
      (promotionDetails ?? null) as SharedPromotionDetails | null
    );
  })();

  const kudozChip =
    resolvedKudozMultiplier !== null ? (
      <Badge
        variant="outline"
        className="text-[10px] px-1.5 py-0 h-5 text-kudoz border-kudoz/50 bg-kudoz/10 flex items-center gap-0.5 flex-shrink-0 backdrop-blur-sm"
        data-testid="kudoz-promo-badge"
      >
        <KudozIcon size="xs" />
        <span>{formatKudozMultiplier(resolvedKudozMultiplier)}</span>
      </Badge>
    ) : null;

  // Expiry info for the Kudoz chip (only shown when there's no coupon code, since the
  // coupon block already renders its own expiry label in the text section)
  const kudozExpiryDate = !promotionDetails?.couponCode && promotionDetails?.expiresAt
    ? promotionDetails.expiresAt
    : null;
  const kudozExpired = kudozExpiryDate ? isOfferExpired(kudozExpiryDate) : false;
  const kudozExpiryLabel = kudozExpiryDate
    ? formatOfferExpiry(kudozExpiryDate)
    : null;
  const kudozExpiryUrgency: OfferExpiryUrgency | null = kudozExpiryDate
    ? getOfferExpiryUrgency(kudozExpiryDate)
    : null;

  return (
    <Card
      ref={attributionRef}
      className="w-full max-w-sm lg:max-w-2xl mx-auto mb-4 overflow-hidden hover-elevate"
      data-testid="restaurant-post-card"
      data-post-id={id}
    >
      {/* Condensed Header: Partner Post from Restaurant */}
      <div className="p-3 pb-1">
        {promotionDetails?.smartFixEdited?.version === 1 &&
          promotionDetails.smartFixEdited.kind === "background-cleanup" &&
          promotionDetails.smartFixEdited.verified === true &&
          promotionDetails.smartFixEdited.assets?.length > 0 && (
            <span className="mb-2 inline-block rounded border px-2 py-0.5 text-xs text-muted-foreground"
              title="One or more photos include SmartFix background cleanup" data-testid="smartfix-edited-badge">
              Edited
            </span>
          )}
        <div className="flex items-center gap-2">
          {/* Restaurant Logo with Partner Crown */}
          <div className="relative flex-shrink-0">
            {restaurant.logo ? (
              <LazyImage
                cdnSize
                src={restaurant.logo}
                alt={restaurant.name}
                width="32"
                height="32"
                className="w-8 h-8 rounded-full object-cover ring-2 ring-amber-400"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center ring-2 ring-amber-400">
                <Crown className="w-4 h-4 text-white" />
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0">
            {/* Partner badge + Restaurant name line */}
            <div className="flex items-center gap-1.5 text-sm">
              <Badge
                variant="outline"
                className="text-[10px] px-1.5 py-0 h-5 text-amber-700 dark:text-amber-400 border-amber-500 bg-amber-100 dark:bg-amber-900/40 flex items-center gap-0.5 flex-shrink-0 font-semibold"
                data-testid="partner-badge"
              >
                <Crown className="w-2.5 h-2.5" />
                Partner Post
              </Badge>
              <span className="text-muted-foreground">from</span>
              <button
                onClick={() => navigate(`/restaurant/${restaurant.id}`)}
                className="hover:underline font-bold truncate"
                data-testid="link-restaurant-name"
              >
                {restaurant.name}
              </button>
            </div>
            {/* Location only - partner posts don't show star ratings */}
            <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
              <MapPin className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{restaurant.location}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Post Content Section - Headline + body */}
      <div className="px-3 pt-1 pb-3">
        {showHeadlineRow && (
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="flex items-center gap-1.5 flex-1 min-w-0">
              {PromotionIcon && promotionDetails?.title && (
                <PromotionIcon className="w-4 h-4 text-primary flex-shrink-0" />
              )}
              {promotionDetails?.title && (
                <h4
                  className="font-bold text-sm truncate"
                  data-testid="text-promotion-title"
                >
                  {promotionDetails.title}
                </h4>
              )}
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {featured && (
                <Badge className="text-[10px] px-1.5 py-0 h-5 bg-amber-500/10 text-amber-600 border-amber-400/50">
                  Featured
                </Badge>
              )}
              {isEvent && eventDate && (
                <Badge
                  variant="outline"
                  className="text-[10px] px-1.5 py-0 h-5 flex items-center gap-1 text-primary border-primary/40 bg-primary/5"
                  data-testid="badge-event-date"
                >
                  <Calendar className="w-2.5 h-2.5" />
                  <span className="truncate max-w-[120px]">{eventDate}</span>
                </Badge>
              )}
            </div>
          </div>
        )}

        <ReadMoreText
          text={text}
          testId="text-post-body"
          prefix={
            !promotionDetails?.title && PromotionIcon ? (
              <PromotionIcon className="inline-block w-4 h-4 text-primary mr-1.5 -mt-0.5 align-text-bottom" />
            ) : null
          }
        />

        {/* Coupon Code */}
        {promotionDetails?.couponCode && (() => {
          const expired = !!promotionDetails.expiresAt && isOfferExpired(promotionDetails.expiresAt);
          const urgency = promotionDetails.expiresAt ? getOfferExpiryUrgency(promotionDetails.expiresAt) : null;
          return (
            <div className={cn("mt-2 inline-flex flex-col gap-0.5", expired && "opacity-60")}>
              <div className="inline-flex items-center gap-1.5">
                <button
                  type="button"
                  className="inline-flex items-center gap-2 bg-amber-100 dark:bg-amber-900/30 rounded-md px-3 py-1.5 border border-amber-300 dark:border-amber-700 cursor-pointer hover-elevate active-elevate-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigator.clipboard.writeText(promotionDetails.couponCode!).then(() => {
                      toast({ description: `Copied ${promotionDetails.couponCode}` });
                      sendAttributionEvent("coupon_code_copied", "post", id, {
                        restaurantId: restaurant.id,
                        metadata: { couponCode: promotionDetails.couponCode! },
                      });
                    });
                  }}
                >
                  <Gift className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                  <span className={cn("text-sm font-bold text-amber-700 dark:text-amber-300", expired && "line-through")}>
                    Use code: {promotionDetails.couponCode}
                  </span>
                  <Copy className="w-3.5 h-3.5 text-amber-500 dark:text-amber-400" />
                </button>
                {typeof navigator !== "undefined" && !!navigator.share && (
                  <button
                    type="button"
                    aria-label="Share coupon code"
                    className="inline-flex items-center justify-center bg-amber-100 dark:bg-amber-900/30 rounded-md p-1.5 border border-amber-300 dark:border-amber-700 cursor-pointer hover-elevate active-elevate-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.share({
                        title: `Coupon code for ${restaurant.name}`,
                        text: `Use code ${promotionDetails.couponCode} at ${restaurant.name}!`,
                      }).then(() => {
                        navigator.clipboard.writeText(promotionDetails.couponCode!).then(() => {
                          toast({ description: "Copied to clipboard too" });
                        }).catch(() => {});
                      }).catch((err: unknown) => {
                        if (err instanceof Error && err.name === "AbortError") {
                          navigator.clipboard.writeText(promotionDetails.couponCode!).then(() => {
                            toast({ description: "Copied to clipboard instead" });
                          }).catch(() => {});
                        } else {
                          toast({ description: "Could not share. Please try again.", variant: "destructive" });
                        }
                      });
                    }}
                  >
                    <Share2 className="w-3.5 h-3.5 text-amber-500 dark:text-amber-400" />
                  </button>
                )}
              </div>
              {promotionDetails.expiresAt && (
                <span
                  className={cn(
                    "text-xs ml-1",
                    urgency === "critical"
                      ? "text-destructive font-medium"
                      : urgency === "warning"
                        ? "text-amber-600 dark:text-amber-400 font-medium"
                        : "text-muted-foreground"
                  )}
                  data-testid="text-offer-expiry"
                >
                  {formatOfferExpiry(promotionDetails.expiresAt)}
                </span>
              )}
            </div>
          );
        })()}

        {/* Kudoz multiplier when no media + no order CTA (otherwise it lives near the order CTA) */}
        {postMedia.length === 0 &&
          restaurant.orderingEnabled === false &&
          kudozChip && (
            <div className="mt-2 inline-flex flex-col gap-0.5">
              {kudozChip}
              {!promotionDetails?.couponCode && promotionDetails?.expiresAt && (
                <span
                  className={cn(
                    "text-xs ml-1",
                    kudozExpiryUrgency === "critical"
                      ? "text-destructive font-medium"
                      : kudozExpiryUrgency === "warning"
                        ? "text-amber-600 dark:text-amber-400 font-medium"
                        : "text-muted-foreground"
                  )}
                  data-testid="text-offer-expiry"
                >
                  {formatOfferExpiry(promotionDetails.expiresAt!)}
                </span>
              )}
            </div>
          )}
      </div>

      {/* Image/Video Carousel - 4:5 portrait matching ReviewCard, with the same
          desktop max-height cap; media uses object-cover to fill the frame. */}
      {postMedia.length > 0 && currentMediaItem && (
        <div className={`relative ${FEED_MEDIA_FRAME_CLASSES} bg-muted overflow-visible`}>
          <div className="w-full h-full overflow-hidden">
            {currentMediaItem.isVideo ? (
              <div
                ref={videoContainerRef}
                className="w-full h-full"
                onClick={() => openLightboxAtCarouselIndex(currentImageIndex)}
              >
                <AuthenticatedVideo
                  src={currentMediaItem.url}
                  className="w-full h-full object-cover"
                  data-testid="post-video"
                  controls
                  autoPlay
                  muted
                  loop
                  playsInline
                />
              </div>
            ) : (
              <button
                className="w-full h-full block cursor-zoom-in focus:outline-none"
                aria-label="View photo fullscreen"
                onClick={() => openLightboxAtCarouselIndex(currentImageIndex)}
              >
                <LazyImage
                  cdnSize
                  src={currentMediaItem.url}
                  alt="Restaurant post"
                  className="w-full h-full"
                  imgClassName="kb-anim"
                  imgStyle={kbStyle}
                  data-testid="post-image"
                />
              </button>
            )}
          </div>

          {/* Item name overlay - bottom left, above tags */}
          {currentItem && <ItemInfoOverlay item={currentItem} />}

          {/* Image Navigation Arrows */}
          {hasMultipleImages && (
            <>
              {currentImageIndex > 0 && (
                <button
                  onClick={() => setCurrentImageIndex((prev) => prev - 1)}
                  className="absolute left-0 top-0 bottom-0 w-20 flex items-center justify-start pl-3 bg-gradient-to-r from-black/20 to-transparent hover:from-black/40 transition-all"
                  data-testid="button-prev-image"
                >
                  <div className="bg-white dark:bg-gray-800 rounded-full p-1.5 shadow-lg">
                    <ChevronLeft className="w-5 h-5 text-gray-900 dark:text-white" />
                  </div>
                </button>
              )}

              {currentImageIndex < postMedia.length - 1 && (
                <button
                  onClick={() => setCurrentImageIndex((prev) => prev + 1)}
                  className="absolute right-0 top-0 bottom-0 w-20 flex items-center justify-end pr-3 bg-gradient-to-l from-black/20 to-transparent hover:from-black/40 transition-all"
                  data-testid="button-next-image"
                >
                  <div className="bg-white dark:bg-gray-800 rounded-full p-1.5 shadow-lg">
                    <ChevronRight className="w-5 h-5 text-gray-900 dark:text-white" />
                  </div>
                </button>
              )}

              {/* Image Indicators */}
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
                {postMedia.map((_, index) => (
                  <button
                    key={index}
                    className={cn(
                      "w-1.5 h-1.5 rounded-full transition-all",
                      index === currentImageIndex
                        ? "bg-white w-4"
                        : "bg-white/60 hover:bg-white/80"
                    )}
                    onClick={() => setCurrentImageIndex(index)}
                    data-testid={`image-indicator-${index}`}
                  />
                ))}
              </div>
            </>
          )}

          {/* Tags Overlay - Bottom left of image */}
          {tags.length > 0 && (
            <div className="absolute bottom-2 left-2 flex flex-wrap gap-1 max-w-[60%]">
              {tags.slice(0, 3).map((tag, index) => (
                <span
                  key={index}
                  className="px-2 py-0.5 bg-black/60 backdrop-blur-sm text-white text-xs rounded-full"
                  data-testid={`tag-${tag}`}
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}

          {/* Order Button + Kudoz multiplier — Top right of image */}
          {restaurant.orderingEnabled !== false ? (
            <div className="absolute top-3 right-3 flex flex-col items-end gap-1.5 z-30">
              <Button
                variant="default"
                size="sm"
                onClick={handleOrder}
                className="h-9 px-3 shadow-lg bg-primary hover:bg-primary/90"
                data-testid="button-order"
              >
                <ShoppingCart className="w-4 h-4 mr-1.5" />
                <span className="text-sm font-medium">Order</span>
              </Button>
              {kudozChip && (
                <div className={cn("flex flex-col items-end gap-0.5", kudozExpired && "opacity-60")}>
                  {kudozChip}
                  {kudozExpiryLabel && (
                    <span
                      className={cn(
                        "text-[9px] leading-tight drop-shadow",
                        kudozExpiryUrgency === "critical"
                          ? "text-red-300 font-medium"
                          : kudozExpiryUrgency === "warning"
                            ? "text-amber-300 font-medium"
                            : "text-white/80"
                      )}
                      data-testid="text-offer-expiry"
                    >
                      {kudozExpiryLabel}
                    </span>
                  )}
                </div>
              )}
            </div>
          ) : (
            // No order CTA: surface Kudoz multiplier on bottom-left so it isn't lost
            kudozChip && (
              <div className={cn("absolute bottom-2 left-2 z-30 flex flex-col gap-0.5", kudozExpired && "opacity-60")}>
                {kudozChip}
                {kudozExpiryLabel && (
                  <span
                    className={cn(
                      "text-[9px] leading-tight drop-shadow",
                      kudozExpiryUrgency === "critical"
                        ? "text-red-300 font-medium"
                        : kudozExpiryUrgency === "warning"
                          ? "text-amber-300 font-medium"
                          : "text-white/80"
                    )}
                    data-testid="text-offer-expiry"
                  >
                    {kudozExpiryLabel}
                  </span>
                )}
              </div>
            )
          )}
        </div>
      )}

      {/* Order Button Banner - Fallback for posts without images */}
      {postMedia.length === 0 && restaurant.orderingEnabled !== false && (
        <div className="px-3 pb-2 flex items-center gap-2">
          <Button
            variant="default"
            size="sm"
            onClick={handleOrder}
            className="flex-1 h-9"
            data-testid="button-order"
          >
            <ShoppingCart className="w-4 h-4 mr-1.5" />
            <span className="text-sm font-medium">
              Order from {restaurant.name}
            </span>
          </Button>
          {kudozChip && (
            <div className={cn("flex flex-col items-end gap-0.5", kudozExpired && "opacity-60")}>
              {kudozChip}
              {kudozExpiryLabel && (
                <span
                  className={cn(
                    "text-[9px] leading-tight",
                    kudozExpiryUrgency === "critical"
                      ? "text-destructive font-medium"
                      : kudozExpiryUrgency === "warning"
                        ? "text-amber-600 dark:text-amber-400 font-medium"
                        : "text-muted-foreground"
                  )}
                  data-testid="text-offer-expiry"
                >
                  {kudozExpiryLabel}
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Compact Action Bar - matching ReviewCard */}
      <div className="p-3 pt-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Helpful - Compact inline */}
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleHelpful(true)}
                className={cn(
                  "p-0 h-auto",
                  helpful === true
                    ? "text-kudoz"
                    : "text-muted-foreground hover:text-foreground"
                )}
                data-testid="button-helpful-up"
              >
                <ThumbsUp
                  className={cn("w-4 h-4", helpful === true && "fill-current")}
                />
              </Button>
              <span className="text-xs text-muted-foreground">{upVotes}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleHelpful(false)}
                className={cn(
                  "p-0 h-auto",
                  helpful === false
                    ? "text-destructive"
                    : "text-muted-foreground hover:text-foreground"
                )}
                data-testid="button-helpful-down"
              >
                <ThumbsDown
                  className={cn("w-4 h-4", helpful === false && "fill-current")}
                />
              </Button>
            </div>

            {/* Share */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleShare}
              className="p-0 h-auto text-muted-foreground hover:text-foreground"
              data-testid="button-share"
            >
              <Share2 className="w-4 h-4" />
            </Button>

            {/* Comment */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleComment}
              className="flex items-center gap-1 p-0 h-auto text-muted-foreground hover:text-foreground"
              data-testid="button-comment"
            >
              <MessageCircle className="w-4 h-4" />
              <span className="text-xs">{comments}</span>
            </Button>
          </div>

          {/* Right side: Save + Timestamp */}
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBookmark}
              className={cn(
                "p-0 h-auto",
                bookmarked
                  ? "text-kudoz"
                  : "text-muted-foreground hover:text-foreground"
              )}
              data-testid="button-bookmark"
            >
              <Bookmark
                className={cn(
                  "w-4 h-4 transition-transform",
                  bookmarked && "fill-current scale-110"
                )}
              />
            </Button>
            <span className="text-xs text-muted-foreground ml-2">
              {createdAt}
            </span>
          </div>
        </div>
      </div>

      {/* Comments Sheet */}
      {showComments && <Suspense fallback={null}><CommentsSheet
        reviewId={id}
        isOpen={showComments}
        onClose={() => setShowComments(false)}
      /></Suspense>}

      {/* Share Modal */}
      {showShareModal && <Suspense fallback={null}><ShareReviewModal
        open={showShareModal}
        onOpenChange={setShowShareModal}
        reviewId={id}
        restaurantName={restaurant.name}
      /></Suspense>}

      {/* Order Initiation Modal */}
      {showOrderModal && (
      <Suspense fallback={null}>
      <OrderInitiationModal
        isOpen={showOrderModal}
        onClose={() => setShowOrderModal(false)}
        onProceed={handleOrderProceed}
        restaurantId={restaurant.id}
        mode="order"
        isRestaurantOpen={isRestaurantOpen}
        timezone={restaurantData?.eatzyTimezone}
        hoursJson={restaurantData?.hoursJson}
        sessionLocationName={sessionLocationName || undefined}
        sessionCoordinates={sessionCoordinates}
        sessionAddressId={sessionAddressId}
        onSessionLocationChange={setSessionLocation}
      />
      </Suspense>
      )}

      {/* Photo Lightbox */}
      {lightboxOpen && <Suspense fallback={null}><MediaLightbox
        entries={lightboxEntries}
        initialIndex={lightboxIndex}
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      /></Suspense>}
    </Card>
  );
}

const RestaurantPostCard = memo(RestaurantPostCardImpl);
export default RestaurantPostCard;
